import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import notify from 'devextreme/ui/notify';
import validationEngine from 'devextreme/ui/validation_engine';
import { getNotificationTimeout, sendNotificationMessage } from '../../../types/resource';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { LoggingService } from '../../../framework/logging/logging.service';
import { lastValueFrom } from 'rxjs';
import { TaskDataService } from '../../../shared/services/task-data.service';
import { IRequestDataBase, ITaskRequest, RequestDataBase, TaskRequest } from '../../../modules/tasks/model/types';
import { MessageService } from '../../../shared/components/framework/services/message.service';
import { FormUtils } from '../../../shared/components/framework/services/form-builder.service';
import { LoanDataBase } from '../baseclass/loandata-base';

@Component({
  standalone: false,
  selector: 'app-suspense',
  templateUrl: './suspense.component.html',
  styleUrls: ['./suspense.component.css'],
  providers: [CurrencyPipe]
})
export class SuspenseComponent extends LoanDataBase implements OnInit {

@ViewChild('formElement') formElement!: ElementRef;

@Input() taskTemplateName: string;
@Input() task: ITaskRequest;

@Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  suspenseData: IRequestDataBase;
  isLoading: boolean = false;
  files: File[] = [];
  requestTypes: any[];
  currencyData: any[];
  sortedCurrencyData: any[] = [];
  currencyEditorOptions: any = {};
  amountEditorOptions: any = {};
  isEditable: boolean = false;


  constructor(
    taskDataService: TaskDataService,
    private readonly messageService: MessageService,
    private readonly loggingService: LoggingService,
    private readonly currencyPipe: CurrencyPipe,
    private cdr: ChangeDetectorRef
  ) {
      super(taskDataService);
   }

  onFileUpload(file: File) {
    this.files.push(file);
  }

  onFileRemove(file: File)
   {
    if(file != null && file != undefined)
    {
      this.files = this.files.filter(f => f !== file);
    }
   }

  onValidateAmount(e: any) : boolean
  {
    if(e.value == null || e.value <= 0)
      return false;

    return true;
  }

  private sortCurrenciesWithPriority(currencies: any[]): any[] {
    const priorityCurrencies = ['USD', 'CAD', 'EUR', 'GBP'];

    // Filter priority currencies that exist in the data
    const priority = currencies.filter(c =>
      priorityCurrencies.includes(c.isoCode || c.code || c.name)
    ).sort((a, b) => {
      const aCode = a.isoCode || a.code || a.name;
      const bCode = b.isoCode || b.code || b.name;
      return priorityCurrencies.indexOf(aCode) - priorityCurrencies.indexOf(bCode);
    });

    // Get remaining currencies sorted alphabetically
    const remaining = currencies.filter(c =>
      !priorityCurrencies.includes(c.isoCode || c.code || c.name)
    ).sort((a, b) => {
      const aName = a.name || a.isoCode || a.code;
      const bName = b.name || b.isoCode || b.code;
      return aName.localeCompare(bName);
    });

    return [...priority, ...remaining];
  }

  getCurrencyFormat(currencyCode: string): string {
    // Use Angular's currency pipe to get proper symbol and formatting
    try {
      // Get the currency symbol using Angular's currency pipe
      const sampleAmount = 0;
      const formatted = this.currencyPipe.transform(sampleAmount, currencyCode, 'symbol-narrow', '1.0-2') || '';

      // Extract the symbol (everything before any digits)
      const symbolMatch = formatted.match(/^([^\d.,]*)/);
      const symbol = symbolMatch ? symbolMatch[1].trim() : currencyCode;

      // Return DevExtreme number format with the extracted symbol
      return symbol ? `${symbol} #,##0.##` : '#,##0.##';
    } catch (error) {
      // Fallback to currency code if pipe fails
      return `${currencyCode} #,##0.##`;
    }
  }

  private initializeEditorOptions() {
    // Initialize currency editor options
    this.currencyEditorOptions = {
      items: this.sortedCurrencyData,
      valueExpr: 'isoCode',
      displayExpr: 'name',
      required: true,
      searchMode: 'contains',
      searchEnabled: true,
      onValueChanged: (e: any) => {
        this.onCurrencyChanged(e.value);
      }
    };

    // Initialize amount editor options
    this.updateAmountEditorOptions();
  }

  private updateAmountEditorOptions() {
    const selectedCurrency = this.sortedCurrencyData?.find(c =>
      (c.isoCode || c.code) === this.suspenseData?.currency
    );
    const currencyCode = selectedCurrency?.isoCode || selectedCurrency?.code || 'USD';
    const currencyFormat = this.getCurrencyFormat(currencyCode);

    this.amountEditorOptions = {
      required: true,
      format: currencyFormat,
      min: 0,
      defaultValue: 0
    };
  }

  onCurrencyChanged(newCurrencyCode: string) {
    // Update the amount field format when currency changes
    this.updateAmountEditorOptions();
    this.cdr.detectChanges();
  }

   async onSubmit(e: any)
  {

        e.preventDefault();

        let validationResult = validationEngine.validateGroup("formGroup");



        if (validationResult.isValid)
        {
          try {
              let request: ITaskRequest = new TaskRequest();
              let requestType =this.requestTypes.find((t) => t.name == this.suspenseData.requestType)
              request.templateId = requestType.id;

              request.name = requestType.name
              request.referenceName = this.suspenseData.loanNumber;
              request.referenceType = requestType.referenceType;

              let formData: FormData = new FormData()
              formData.append('templateId', request.templateId);
              formData.append('name', request.name);
              formData.append('referenceName', request.referenceName);
              formData.append('requestJsonData', JSON.stringify(this.appendDealNameToSubmission({
                ...this.suspenseData
              })));
              formData.append('referenceType', request.referenceType);

              if(this.isEditable)
                  formData.append('parentId', this.task.parentId);

              this.files.forEach((f) => { formData.append('files', f, f.name) });

              this.isLoading = true;

              const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
              sendNotificationMessage(`${response[1].data[0].task.taskId} task created`, this.messageService);

              this.isLoading = false;

              this.formElement.nativeElement.reset();
              this.formElement.nativeElement.submit();
            }
            catch (err: any) {
              this.isLoading = false;
              this.loggingService.logError(err, SeverityLevel.Error);
              notify(err.message, 'error', getNotificationTimeout());
            }
        }
  }

  override async ngOnInit() {

    try{
      let requestData =  await lastValueFrom( this.taskDataService.getTaskTemplatesByType(this.taskTemplateName));
      this.requestTypes = requestData[1];

      await super.ngOnInit();

      const currencyData = await lastValueFrom(this.taskDataService.getCurrencies());
      this.currencyData = currencyData[1];

      // Sort currencies with USD, CAD, EUR, GBP first, then alphabetically
      this.sortedCurrencyData = this.sortCurrenciesWithPriority(this.currencyData);

      // Initialize editor options after currency data is loaded
      this.initializeEditorOptions();

      this.isEditable = this.task != null ? true : false;

      if(this.isEditable)
      {
        this.suspenseData = FormUtils.mapFromTask<RequestDataBase>(this.task, RequestDataBase);
        // console.log(this.suspenseData);
      }
      else{
        this.suspenseData = new RequestDataBase();
      }



      // Use setTimeout to ensure the form is fully rendered before setting selected index
      setTimeout(() => {
        if (this.requestTypes && this.requestTypes.length > 0) {
          this.suspenseData.requestType = this.requestTypes[0].name;
          // console.log('Set request type to:', this.suspenseData.requestType);
        }

        // Set default currency to USD
        if (this.sortedCurrencyData && this.sortedCurrencyData.length > 0) {
          const usdCurrency = this.sortedCurrencyData.find(c =>
            (c.isoCode || c.code || c.name) === 'USD'
          );
          this.suspenseData.currency = (usdCurrency?.isoCode || usdCurrency?.code) ||
                                      (this.sortedCurrencyData[0]?.isoCode || this.sortedCurrencyData[0]?.code);

          // Update amount editor options with the default currency
          this.updateAmountEditorOptions();
        }

        this.cdr.detectChanges();
      }, 100);

    }
    catch(err: any)
    {
      this.isLoading = false;
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

}
